import React, { useState, useEffect } from 'react';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';

const MACHINE_CODES = [
  "SUPERSEEDER", "ROTAVATOR", "MULCHER", "RMBPLOUGH", "ZEROTILLDRILL", "ROTARYSLASHER", 
  "PADDYSTRAWCHOPPER", "BALER", "RAKER", "REAPER", "TRACTOR", "HAPPYSEEDER", "SURFACESEEDER",
  "SMARTSEEDER", "SUPERSMS", "SHRUBMASTER", "CUTTERCUMSPREADER", "TRACTORTROLLEY", "LASERLEVELLER",
  "DIRECTSEEDERRICE", "MAIZEPLANTERMECHANICAL", "MAIZEPLANTERVACCUM", "SPRAYPUMP", "PRESSUREDISKS",
  "DISKLOCK", "BIOFERTILIZERSPREADER", "CULTIVATOR", "SUBSOILER", "POWERCLEANER", "DISCRAZOR",
  "HARROWTRIPPLE", "COTTONSEEDDRILL", "WATERTANK", "SUHAGA", "WHEATSEEDFAN", "SEEDGRINDINGMACHINE",
  "JINDRA", "COMBINE", "SEELARCOTTON", "GENERATOR", "WHEATSEEDDRILL", "PLOUGH", "GROUNDLEVELLER",
  "STRAWREAPER", "POTATOPLANTER", "TAWIAN", "SIMPLELEVELLER", "LOADER", "POTATODRILL",
  "MULTIDRILLMACHINE", "PUDDLER", "CHOPPER", "CORNDRILL", "PEASMACHINE", "SEEDDRILL", "CHIZLER",
  "PADDYPLANTER", "DIESELTANK", "BHAUNI", "TOTA", "TILLER", "KARAH", "TARFALI", "RAZOR",
  "THRESHER", "KARCHAMACHINE", "RATOR", "SUGARCANERAGER", "DISCHARROW", "CHAINKUPPI",
  "SUGARCANESEEDER", "GRINDER", "CHANGER", "TRIPPLEPLOUGH", "CORNHARVESTER", "CUTTER",
  "MOTORMACHINE", "DISCPLOUGH", "GAINIPLOUGH", "DUGPLOUGH", "OFFSETHARROW", "JAAL",
  "TARAKADI", "CARROTMACHINE", "DISCBEDMAKER", "LOADERBUCKET", "SEEDGRADER", "ACHATHALL",
  "TAWAWATTA", "JACK"
];

const ZeroTransactionsReportPage = ({ user }) => {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Fetch Periods on mount
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/periods`);
        const data = await res.json();
        if (data.success && Array.isArray(data.periods) && data.periods.length > 0) {
          setPeriods(data.periods);
          const latest = data.periods[0];
          setSelectedPeriodId(latest._id);
          setSelectedPeriod(latest);
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
      }
    };
    fetchPeriods();
  }, []);

  const handlePeriodChange = (e) => {
    const periodId = e.target.value;
    setSelectedPeriodId(periodId);
    const matched = periods.find(p => p._id === periodId);
    if (matched) {
      setSelectedPeriod(matched);
    }
  };

  const handleExportZeroTransactions = async () => {
    if (!selectedPeriodId) return;
    try {
      setExporting(true);
      const res = await fetch(`${API_BASE_URL}/api/reports/zero-transactions-societies?periodId=${selectedPeriodId}`);
      const data = await res.json();

      if (data.success && Array.isArray(data.societies)) {
        const headers = [
          "Society Name", "Division", "District", "AR Circle", "Village", 
          "DR Name", "DR Mobile", "DR Email", 
          "AR Name", "AR Mobile", "AR Email", 
          "Inspector Name", "Inspector Mobile", "Inspector Email", 
          "Secretary Name", "Secretary Mobile", "Secretary Email",
          "Acres Covered", "Number of Members",
          "Total Machines", "Total Authorized Machines"
        ];
        MACHINE_CODES.forEach(code => {
          headers.push(`${code} Count`);
          headers.push(`${code} Authorized Count`);
        });

        const csvRows = [headers.join(',')];

        data.societies.forEach(s => {
          const line = [
            `"${s.SOCIETY || s.SocietyName || ''}"`,
            `"${s.DIVISION || ''}"`,
            `"${s.DISTRICT || ''}"`,
            `"${s.AR || s.ARNAME || ''}"`,
            `"${s.VILLAGE || ''}"`,
            `"${s.DRNAME || ''}"`,
            `"${s.DRMOBILE || ''}"`,
            `"${s.DREMAILID || ''}"`,
            `"${s.ARNAME || ''}"`,
            `"${s.ARMOBILE || ''}"`,
            `"${s.AREMAILID || ''}"`,
            `"${s.INSPECTOR || ''}"`,
            `"${s.INSPECTORMOBILE || ''}"`,
            `"${s.INSPECTOREMAILID || ''}"`,
            `"${s.SECRETARY || ''}"`,
            `"${s.SECRETARYMOBILE || ''}"`,
            `"${s.SECRETARYEMAILID || ''}"`
          ];

          const mCounts = s.machineCounts || {};
          const aCounts = s.authMachineCounts || {};

          const acresCovered = mCounts.ACRES ?? mCounts.acres ?? s.ACRES ?? 0;
          const noOfMembers = mCounts.NO_OF_MEMBERS ?? mCounts.membersCount ?? s.NO_OF_MEMBERS ?? 0;

          line.push(acresCovered, noOfMembers);

          const totalM = mCounts.TOTALMACHINES !== undefined ? mCounts.TOTALMACHINES : Object.keys(mCounts).reduce((sum, key) => (typeof mCounts[key] === 'number' && key !== '__v' ? sum + mCounts[key] : sum), 0);
          const totalA = Object.keys(aCounts).reduce((sum, key) => (typeof aCounts[key] === 'number' && key !== '__v' ? sum + aCounts[key] : sum), 0);

          line.push(totalM, totalA);

          MACHINE_CODES.forEach(code => {
            line.push(mCounts[code] || 0);
            line.push(aCounts[code] || 0);
          });

          csvRows.push(line.join(','));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const periodName = selectedPeriod?.seasonName || selectedPeriod?.PERIOD || 'Period';
        link.setAttribute("download", `Societies_Zero_Transactions_${periodName}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting zero transactions societies:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <ListContainer title="Export Societies with Zero Transactions">
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '32px 24px',
        maxWidth: '680px',
        margin: '20px auto',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
          Zero Transactions Societies Report
        </h2>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '28px', lineHeight: '1.5' }}>
          Download a complete CSV report listing all cooperative societies where no machine usage transactions were captured during the selected period.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', maxWidth: '400px' }}>
            <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155', whiteSpace: 'nowrap' }}>
              Select Period:
            </label>
            <select
              value={selectedPeriodId}
              onChange={handlePeriodChange}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                fontWeight: '500',
                color: '#0f172a'
              }}
            >
              {periods.map(p => {
                const label = `${p.StartDate} to ${p.EndDate} (${p.seasonName || p.PERIOD})`;
                return <option key={p._id} value={p._id}>{label}</option>;
              })}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExportZeroTransactions}
            disabled={exporting || !selectedPeriodId}
            style={{
              background: exporting ? '#94a3b8' : '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 28px',
              fontWeight: '600',
              fontSize: '15px',
              cursor: exporting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: exporting ? 'none' : '0 4px 10px rgba(239, 68, 68, 0.25)',
              transition: 'all 0.2s ease'
            }}
          >
            {exporting ? '⏳ Exporting CSV Report...' : '📥 Download CSV Report'}
          </button>
        </div>
      </div>
    </ListContainer>
  );
};

export default ZeroTransactionsReportPage;
